import repository from '@/repository'
import { ActionTree, Module, MutationTree, GetterTree } from 'vuex'
import { RootState } from '@/store/types'

const getDefaultState = (): CustomerTriggeredActionsState => {
  return {
    customerTriggeredActionsList: [],
    customerTriggeredActionsListIsLoading: false,
    customerTriggeredActionsListIsError: false,
    customerTriggeredActionsListErrorMessage: '',
    customerTriggeredActionsTypes: [],
    addCustomerTriggeredActionIsError: false,
    addCustomerTriggeredActionErrorMessage: '',
    deleteCustomerTriggeredActionIsError: false,
    deleteCustomerTriggeredActionErrorMessage: '',
    updateCustomerTriggeredActionIsError: false,
    updateCustomerTriggeredActionErrorMessage: '',
    availableDeviceVcaRules: [],
    availableDeviceVcaRulesIsLoading: false,
    availableDeviceVcaRulesIsError: false,
    availableDeviceVcaRulesErrorMessage: '',
    deviceLightColors: [],
    triggeredActionConfigureIsLoading: false,
    deviceLightOptions: [],
    deviceResourceList: [],
    exportTriggeredActionsIsLoading: false,
  }
}

const state = getDefaultState()

const getters: GetterTree<CustomerTriggeredActionsState, RootState> = {
  deviceResourceSchedules(state): any[] {
    return state.deviceResourceList.filter((item) => item.resourceTypeId === 4)
  },
  deviceResourceAudio(state): any[] {
    return state.deviceResourceList.filter(
      (item) => item.resourceTypeId === 1 || item.resourceTypeId === 2
    )
  },
}

const mutations: MutationTree<CustomerTriggeredActionsState> = {
  reset(state) {
    Object.assign(state, getDefaultState())
  },
  setCustomerTriggeredActionsList(state, value) {
    state.customerTriggeredActionsList = value
  },
  setCustomerTriggeredActionsListLoading(state, value) {
    state.customerTriggeredActionsListIsLoading = value
  },
  setCustomerTriggeredActionsListIsError(state, value) {
    state.customerTriggeredActionsListIsError = value
  },
  setCustomerTriggeredActionsListErrorMessage(state, value) {
    state.customerTriggeredActionsListErrorMessage = value
  },
  setCustomerTriggeredActionsTypes(state, value) {
    state.customerTriggeredActionsTypes = value
  },
  setAddCustomerTriggeredActionIsError(state, value) {
    state.addCustomerTriggeredActionIsError = value
  },
  setAddCustomerTriggeredActionErrorMessage(state, value) {
    state.addCustomerTriggeredActionErrorMessage = value
  },
  setDeleteCustomerTriggeredActionIsError(state, value) {
    state.deleteCustomerTriggeredActionIsError = value
  },
  setDeleteCustomerTriggeredActionErrorMessage(state, value) {
    state.deleteCustomerTriggeredActionErrorMessage = value
  },
  setAvailableDeviceVcaRules(state, value) {
    state.availableDeviceVcaRules = value
  },
  setAvailableDeviceVcaRulesIsLoading(state, value) {
    state.availableDeviceVcaRulesIsLoading = value
  },
  setAvailableDeviceVcaRulesIsError(state, value) {
    state.availableDeviceVcaRulesIsError = value
  },
  setAvailableDeviceVcaRulesErrorMessage(state, value) {
    state.availableDeviceVcaRulesErrorMessage = value
  },
  setDeviceLightColors(state, value) {
    state.deviceLightColors = value
  },
  setTriggeredActionConfigureIsLoading(state, value) {
    state.triggeredActionConfigureIsLoading = value
  },
  setDeviceLightOptions(state, value) {
    state.deviceLightOptions = value
  },
  setDeviceResourceList(state, value) {
    state.deviceResourceList = value
  },
  setUpdateCustomerTriggeredActionIsError(state, value) {
    state.updateCustomerTriggeredActionIsError = value
  },
  setUpdateCustomerTriggeredActionErrorMessage(state, value) {
    state.updateCustomerTriggeredActionErrorMessage = value
  },
  setUpdateCustomerTriggeredActionsList(state, value) {
    state.customerTriggeredActionsList = state.customerTriggeredActionsList.map(
      (item) => (item.deviceTriggerId === value.deviceTriggerId ? value : item)
    )
  },
  setExportTriggeredActionsIsLoading(state, value) {
    state.exportTriggeredActionsIsLoading = value
  },
}

const actions: ActionTree<CustomerTriggeredActionsState, RootState> = {
  async getDeviceTriggers({ commit }, payload) {
    commit('setCustomerTriggeredActionsListLoading', true)
    commit('setCustomerTriggeredActionsListIsError', false)
    return repository.DeviceTrigger.GetDeviceTriggers(payload)
      .then((res) => {
        commit('setCustomerTriggeredActionsList', res.data)
      })
      .catch((error) => {
        commit('setCustomerTriggeredActionsListIsError', true)
        commit(
          'setCustomerTriggeredActionsListErrorMessage',
          error.response.data.messageLocalizable
        )
      })
      .finally(() => {
        commit('setCustomerTriggeredActionsListLoading', false)
      })
  },
  async getTriggeredTypes({ commit }, payload) {
    commit('setCustomerTriggeredActionsListLoading', true)
    commit('setCustomerTriggeredActionsListIsError', false)
    return repository.DeviceTrigger.GetDeviceTriggerTypes(payload)
      .then((res) => {
        commit('setCustomerTriggeredActionsTypes', res.data)
      })
      .catch((error) => {
        commit('setCustomerTriggeredActionsListIsError', true)
        commit(
          'setCustomerTriggeredActionsListErrorMessage',
          error.response.data.messageLocalizable
        )
      })
      .finally(() => {
        commit('setCustomerTriggeredActionsListLoading', false)
      })
  },
  async getAvailableDeviceVcaRules({ commit }, payload) {
    commit('setAvailableDeviceVcaRulesIsLoading', true)
    commit('setAvailableDeviceVcaRulesIsError', false)
    return repository.DeviceTrigger.GetAvailableDeviceVcaRules(payload)
      .then((res) => {
        commit('setAvailableDeviceVcaRules', res.data)
      })
      .catch((error) => {
        commit('setAvailableDeviceVcaRulesIsError', true)
        commit(
          'setAvailableDeviceVcaRulesErrorMessage',
          error.response.data.messageLocalizable || error.message
        )
      })
      .finally(() => {
        commit('setAvailableDeviceVcaRulesIsLoading', false)
      })
  },
  async getDeviceLightColors({ commit }, payload) {
    commit('setTriggeredActionConfigureIsLoading', true)
    return repository.DeviceTrigger.GetDeviceLightColors(payload)
      .then((res) => {
        commit('setDeviceLightColors', res.data)
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => {
        commit('setTriggeredActionConfigureIsLoading', false)
      })
  },
  async getDeviceLightOptions({ commit }, payload) {
    commit('setTriggeredActionConfigureIsLoading', true)
    return repository.DeviceTrigger.GetDeviceLightOptions(payload)
      .then((res) => {
        commit('setDeviceLightOptions', res.data)
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => {
        commit('setTriggeredActionConfigureIsLoading', false)
      })
  },
  async getDeviceSchedules({ commit }, payload) {
    commit('setTriggeredActionConfigureIsLoading', true)
    return repository.Resource.GetResources(payload)
      .then((res) => {
        commit('setDeviceResourceList', res.data)
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => {
        commit('setTriggeredActionConfigureIsLoading', false)
      })
  },

  async addCustomerTriggeredAction({ commit }, payload) {
    commit('setAddCustomerTriggeredActionIsError', false)
    commit('setCustomerTriggeredActionsListLoading', true)
    return repository.DeviceTrigger.CreateDeviceTrigger(payload)
      .then((res) => {
        return res.data
      })
      .catch((err) => {
        commit('setAddCustomerTriggeredActionIsError', true)
        commit(
          'setAddCustomerTriggeredActionErrorMessage',
          err.response.data.messageLocalizable
        )
      })
      .finally(() => {
        commit('setCustomerTriggeredActionsListLoading', false)
      })
  },
  async updateCustomerTriggeredAction({ commit }, payload) {
    commit('setAddCustomerTriggeredActionIsError', false)
    commit('setCustomerTriggeredActionsListLoading', true)
    return repository.DeviceTrigger.UpdateDeviceTrigger(payload)
      .then((res) => {
        return res.data
      })
      .catch((err) => {
        commit('setAddCustomerTriggeredActionIsError', true)
        commit(
          'setAddCustomerTriggeredActionErrorMessage',
          err.response.data.messageLocalizable
        )
      })
      .finally(() => {
        commit('setCustomerTriggeredActionsListLoading', false)
      })
  },
  async deleteCustomerTriggeredAction({ commit }, payload) {
    commit('setDeleteCustomerTriggeredActionIsError', false)
    commit('setCustomerTriggeredActionsListLoading', true)
    return repository.DeviceTrigger.DeleteDeviceTrigger(payload)
      .then((res) => {
        return res.data
      })
      .catch((err) => {
        commit('setDeleteCustomerTriggeredActionIsError', true)
        commit(
          'setDeleteCustomerTriggeredActionErrorMessage',
          err.response.data.messageLocalizable
        )
      })
      .finally(() => {
        commit('setCustomerTriggeredActionsListLoading', false)
      })
  },
  async deactivateDeviceTrigger({ commit }, payload) {
    commit('setCustomerTriggeredActionsListLoading', true)
    commit('setUpdateCustomerTriggeredActionIsError', false)
    return repository.DeviceTrigger.DeactivateDeviceTrigger(payload)
      .then((res) => {
        if (res.data) {
          commit('setUpdateCustomerTriggeredActionsList', res.data)
        }
      })
      .catch((err) => {
        commit('setUpdateCustomerTriggeredActionIsError', true)
        commit(
          'setUpdateCustomerTriggeredActionErrorMessage',
          err.response.data.messageLocalizable
        )
      })
      .finally(() => {
        commit('setCustomerTriggeredActionsListLoading', false)
      })
  },
  async activateDeviceTrigger({ commit }, payload) {
    commit('setCustomerTriggeredActionsListLoading', true)
    commit('setUpdateCustomerTriggeredActionIsError', false)
    return repository.DeviceTrigger.ActivateDeviceTrigger(payload)
      .then((res) => {
        if (res.data) {
          commit('setUpdateCustomerTriggeredActionsList', res.data)
        }
      })
      .catch((err) => {
        commit('setUpdateCustomerTriggeredActionIsError', true)
        commit(
          'setUpdateCustomerTriggeredActionErrorMessage',
          err.response.data.messageLocalizable
        )
      })
      .finally(() => {
        commit('setCustomerTriggeredActionsListLoading', false)
      })
  },
  getCustomerDeviceTriggerReport({ commit }, payload) {
    commit('setExportTriggeredActionsIsLoading', true)
    return repository.DeviceTrigger.GetCustomerDeviceTriggerReport(payload)
      .then((res) => {
        return res
      })
      .catch((err) => {
        console.error(err)
      })
      .finally(() => {
        commit('setExportTriggeredActionsIsLoading', false)
      })
  },
  async retryDeviceTrigger({ commit }, payload) {
    commit('setCustomerTriggeredActionsListLoading', true)
    commit('setUpdateCustomerTriggeredActionIsError', false)
    return repository.DeviceTrigger.RetryDeviceTrigger(payload)
      .then((res) => {
        if (res.data) {
          commit('setUpdateCustomerTriggeredActionsList', res.data)
          return res.data
        }
      })
      .catch((err) => {
        commit('setUpdateCustomerTriggeredActionIsError', true)
        commit(
          'setUpdateCustomerTriggeredActionErrorMessage',
          err.response.data.messageLocalizable
        )
      })
      .finally(() => {
        commit('setCustomerTriggeredActionsListLoading', false)
      })
  },
}

const CustomerTriggeredActions: Module<
  CustomerTriggeredActionsState,
  RootState
> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}

export default CustomerTriggeredActions

interface CustomerTriggeredActionsState {
  customerTriggeredActionsList: any[]
  customerTriggeredActionsListIsLoading: boolean
  customerTriggeredActionsListIsError: boolean
  customerTriggeredActionsListErrorMessage: string
  customerTriggeredActionsTypes: any[]
  addCustomerTriggeredActionIsError: boolean
  addCustomerTriggeredActionErrorMessage: string
  updateCustomerTriggeredActionIsError: boolean
  updateCustomerTriggeredActionErrorMessage: string
  deleteCustomerTriggeredActionIsError: boolean
  deleteCustomerTriggeredActionErrorMessage: string
  availableDeviceVcaRules: any[]
  availableDeviceVcaRulesIsLoading: boolean
  availableDeviceVcaRulesIsError: boolean
  availableDeviceVcaRulesErrorMessage: string
  deviceLightColors: any[]
  triggeredActionConfigureIsLoading: boolean
  deviceLightOptions: any[]
  deviceResourceList: any[]
  exportTriggeredActionsIsLoading: boolean
}
